import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as projectService from "../services/projectService";
import * as projectInvestmentService from "../services/projectInvestmentService";
import * as accountService from "../services/accountService";
import type {
  Project,
  ProjectInvestmentOption,
  Account,
  ProjectInvestment,
} from "../types/api";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { useToast } from "../components/ui/use-toast";
import { resolveAssetUrl } from "../utils/url";
import { useTranslation } from "react-i18next";
import {
  pickProjectDescription,
  pickProjectName,
} from "../utils/localeDisplay";
import { useAuth } from "../hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedOption, setSelectedOption] =
    useState<ProjectInvestmentOption | null>(null);
  const [isInvesting, setIsInvesting] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showPackDialog, setShowPackDialog] = useState(false);
  const [transferAmount, setTransferAmount] = useState<number>(0);
  const [userInvestments, setUserInvestments] = useState<ProjectInvestment[]>(
    []
  );
  const [now, setNow] = useState(() => new Date());
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!projectId) {
      toast({
        title: "Projet introuvable",
        description: "Identifiant manquant.",
        variant: "destructive",
      });
      return;
    }
    const fetchProject = async () => {
      try {
        const data = await projectService.getProjectById(Number(projectId));
        setProject(data);
        if (data.investmentOptions && data.investmentOptions.length > 0) {
          setSelectedOption(data.investmentOptions[0]);
        }
      } catch (err) {
        toast({
          title: "Erreur",
          description: (err as string) ?? "Projet introuvable",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    void fetchProject();
  }, [projectId, toast]);

  // Charger les comptes de l'utilisateur
  useEffect(() => {
    if (isAuthenticated && user) {
      const fetchAccounts = async () => {
        try {
          const userAccounts = await accountService.getUserAccounts();
          setAccounts(userAccounts);
        } catch (err) {
          console.error("Erreur lors du chargement des comptes:", err);
        }
      };
      void fetchAccounts();
    }
  }, [isAuthenticated, user]);

  // Fonction pour charger les investissements (réutilisable)
  const fetchUserInvestments = useCallback(async () => {
    if (!isAuthenticated || !user || !projectId) return;

    try {
      const investments =
        await projectInvestmentService.getUserProjectInvestments();
      console.log("Tous les investissements récupérés:", investments);
      // Filtrer pour ne garder que les investissements sur ce projet avec statut actif
      const projectInvestments = investments.filter((inv) => {
        const matchesProject = inv.projectId === Number(projectId);
        const isActive = inv.status === "active";
        console.log(
          `Investissement ${inv.id}: projectId=${
            inv.projectId
          } (attendu: ${projectId}), status=${inv.status}, matches=${
            matchesProject && isActive
          }`
        );
        return matchesProject && isActive;
      });
      console.log(
        "Investissements filtrés pour ce projet:",
        projectInvestments
      );
      setUserInvestments(projectInvestments);
    } catch (err) {
      console.error("Erreur lors du chargement des investissements:", err);
    }
  }, [isAuthenticated, user, projectId]);

  // Charger les investissements de l'utilisateur sur ce projet
  useEffect(() => {
    void fetchUserInvestments();
  }, [fetchUserInvestments]);

  useEffect(() => {
    if (!project?.openingDate || project.status !== "upcoming") {
      return;
    }
    setNow(new Date());
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, [project?.openingDate, project?.status]);

  // Fonction pour calculer les gains réels
  const calculateInvestmentProgress = (investment: ProjectInvestment) => {
    if (!investment.maturityDate) {
      return {
        currentGain: 0,
        progressPercent: 0,
        daysRemaining: 0,
        isMature: false,
      };
    }

    const now = new Date();
    const investedAt = new Date(investment.investedAt);
    const maturityDate = new Date(investment.maturityDate);

    const totalDays = Math.ceil(
      (maturityDate.getTime() - investedAt.getTime()) / (1000 * 60 * 60 * 24)
    );
    const daysElapsed = Math.floor(
      (now.getTime() - investedAt.getTime()) / (1000 * 60 * 60 * 24)
    );
    const daysRemaining = Math.max(
      0,
      Math.ceil(
        (maturityDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      )
    );

    const isMature = now >= maturityDate;
    const progressPercent =
      totalDays > 0
        ? Math.min(100, Math.max(0, (daysElapsed / totalDays) * 100))
        : 100;

    const currentGain = isMature
      ? investment.expectedReturn
      : (investment.expectedReturn * progressPercent) / 100;

    return { currentGain, progressPercent, daysRemaining, isMature };
  };

  // Calculer le pourcentage de progression avec une précision minimale
  const progress =
    project && project.goal > 0
      ? Math.min(Math.max(0, (project.totalRaised / project.goal) * 100), 100)
      : 0;

  // Arrondir pour l'affichage, mais garder au moins 0.01% si totalRaised > 0
  const progressDisplay =
    project && project.totalRaised > 0 && progress < 0.01
      ? 0.01
      : Math.round(progress * 100) / 100; // Arrondir à 2 décimales

  const displayName = project
    ? pickProjectName(project, i18n.language)
    : "";
  const displayDescription = project
    ? pickProjectDescription(project, i18n.language)
    : "";

  const openingDate = project?.openingDate
    ? new Date(project.openingDate)
    : null;

  const countdown = useMemo(() => {
    if (!openingDate || Number.isNaN(openingDate.getTime())) {
      return null;
    }
    const diff = openingDate.getTime() - now.getTime();
    const clamped = Math.max(0, diff);
    const totalSeconds = Math.floor(clamped / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return {
      isStarted: diff <= 0,
      days,
      hours,
      minutes,
      seconds,
      dateLabel: openingDate.toLocaleString(
        i18n.language.startsWith("fr")
          ? "fr-FR"
          : i18n.language.startsWith("de")
          ? "de-DE"
          : i18n.language.startsWith("pt")
          ? "pt-BR"
          : "en-US",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      ),
    };
  }, [openingDate, now, i18n.language]);

  const mockAnalytics = useMemo(() => {
    if (!project) {
      return [];
    }
    return [
      {
        label: t("projectDetail.analyticsContributors"),
        value: String(project.activeContributors || 0),
      },
    ];
  }, [project, t]);

  const graphImages = useMemo(
    () =>
      (project?.images ?? [])
        .map((img) => resolveAssetUrl(img.imageUrl))
        .filter((url): url is string => Boolean(url)),
    [project]
  );

  const handleInvest = async () => {
    if (!selectedOption || !project) {
      return;
    }
    if (!isAuthenticated || !user) {
      toast({
        title: t("auth.loginRequiredTitle", "Connexion requise"),
        description: t(
          "auth.loginRequiredDescription",
          "Connectez-vous pour pouvoir investir sur un projet."
        ),
        variant: "destructive",
      });
      return;
    }

    // Vérification locale du solde avant tout appel serveur
    const tradingAcc = accounts.find(
      (acc) => acc.type === "trading" && acc.currency === "USD"
    );
    const tradingAvailable = tradingAcc
      ? tradingAcc.balance - tradingAcc.lockedBalance
      : 0;

    if (tradingAvailable < selectedOption.amount) {
      const mainAcc = accounts.find(
        (acc) => acc.type === "main" && acc.currency === "USD"
      );
      const mainBalance = mainAcc
        ? mainAcc.balance - mainAcc.lockedBalance
        : 0;
      if (mainBalance >= selectedOption.amount) {
        setTransferAmount(selectedOption.amount);
        setShowTransferDialog(true);
      } else {
        setShowPackDialog(true);
      }
      return;
    }

    try {
      setIsInvesting(true);
      await projectInvestmentService.investInProject(
        project.id,
        selectedOption.id
      );

      toast({
        title: t("projectDetail.investSuccessTitle", "Investissement réussi"),
        description: t(
          "projectDetail.investSuccessDescription",
          "Votre investissement a été effectué avec succès. Les fonds ont été débités de votre compte trading."
        ),
        variant: "success",
      });

      // Attendre un peu pour laisser le temps à la base de données de s'actualiser
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Recharger les comptes pour mettre à jour les soldes
      const userAccounts = await accountService.getUserAccounts();
      setAccounts(userAccounts);

      // Recharger le projet pour mettre à jour le total_raised
      const updatedProject = await projectService.getProjectById(project.id);
      setProject(updatedProject);

      // Recharger les investissements de l'utilisateur sur ce projet
      await fetchUserInvestments();
    } catch (error: any) {
      console.error("Erreur lors de l'investissement:", error);

      const errorMessage =
        error?.response?.data?.message || error?.message || "";

      // Vérifier si l'erreur indique des fonds insuffisants
      if (
        errorMessage.includes("Fonds insuffisants") ||
        errorMessage.includes("Solde insuffisant")
      ) {
        // Vérifier si l'utilisateur a des fonds dans le compte principal
        const mainAccount = accounts.find(
          (acc) => acc.type === "main" && acc.currency === "USD"
        );
        const mainBalance = mainAccount
          ? mainAccount.balance - mainAccount.lockedBalance
          : 0;

        if (mainBalance >= selectedOption.amount) {
          // L'utilisateur peut transférer depuis le compte principal
          setTransferAmount(selectedOption.amount);
          setShowTransferDialog(true);
        } else {
          // L'utilisateur doit acheter un pack
          setShowPackDialog(true);
        }
      } else {
        toast({
          title: t(
            "projectDetail.investErrorTitle",
            "Investissement impossible"
          ),
          description:
            errorMessage ||
            t(
              "projectDetail.investErrorDescription",
              "Une erreur est survenue lors de l'investissement."
            ),
          variant: "destructive",
        });
      }
    } finally {
      setIsInvesting(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedOption) return;

    try {
      await accountService.transferBetweenAccounts({
        direction: "main_to_trading",
        amount: transferAmount,
        currency: "USD",
      });

      toast({
        title: t("projectDetail.transferSuccessTitle", "Transfert réussi"),
        description: t(
          "projectDetail.transferSuccessDescription",
          "Les fonds ont été transférés vers votre compte trading."
        ),
        variant: "success",
      });

      // Recharger les comptes
      const updatedAccounts = await accountService.getUserAccounts();
      setAccounts(updatedAccounts);

      setShowTransferDialog(false);

      // Réessayer l'investissement automatiquement
      setTimeout(async () => {
        await handleInvest();
        // Recharger les investissements après l'investissement
        await fetchUserInvestments();
      }, 500);
    } catch (error: any) {
      toast({
        title: t("projectDetail.transferErrorTitle", "Erreur de transfert"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          t(
            "projectDetail.transferErrorDescription",
            "Une erreur est survenue lors du transfert."
          ),
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <Skeleton className="h-64 rounded-3xl" />;
  }

  if (!project) {
    return null;
  }

  const hasOptions =
    project.investmentOptions && project.investmentOptions.length > 0;
  const isProjectOpen = project.status === "open";
  const isProjectUpcoming = project.status === "upcoming";
  const canInvest = isProjectOpen && (!countdown || countdown.isStarted);

  const tradingAccountUSD = accounts.find(
    (a) => a.type === "trading" && a.currency === "USD"
  );
  const tradingAvailableUSD = tradingAccountUSD
    ? tradingAccountUSD.balance - tradingAccountUSD.lockedBalance
    : 0;
  const hasInsufficientTradingFunds =
    isAuthenticated &&
    selectedOption != null &&
    tradingAvailableUSD < selectedOption.amount;
  const statusLabel =
    project.status === "open"
      ? t("projectDetail.statusOpen", "Ouvert")
      : project.status === "closed"
      ? t("projectDetail.statusClosed", "Clôturé")
      : t("projectDetail.statusUpcoming", "À venir");
  const statusClass =
    project.status === "open"
      ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-900 dark:text-emerald-200"
      : project.status === "closed"
      ? "border-rose-400/40 bg-rose-500/15 text-rose-900 dark:text-rose-200"
      : "border-amber-400/40 bg-amber-500/15 text-amber-900 dark:text-amber-200";

  return (
    <section className="space-y-6 rounded-3xl border border-border bg-muted/50 p-6 text-foreground dark:border-white/10 dark:bg-black/30 dark:text-white">
      <Link to="/projects" className="text-sm text-muted-foreground hover:text-foreground dark:text-white/60 dark:hover:text-white">
        {t("projectDetail.back")}
      </Link>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.4em] text-cyan-300">
            {t("projectDetail.projectPrefix")} #{project.symbol}
          </p>
          <h1 className="text-3xl font-semibold">{displayName}</h1>
          <div className="mt-2 inline-flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-current opacity-80" />
            <span
              className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-widest ${statusClass}`}>
              {statusLabel}
            </span>
          </div>
        </div>
        <Button
          className="w-full sm:w-auto"
          onClick={() => navigate("/support")}>
          {t("projectDetail.contactTeam")}
        </Button>
      </div>
      <p className="text-foreground/90 dark:text-foreground/90 dark:text-white/80">{displayDescription}</p>

      {project.status === "upcoming" && countdown && (
        <div className="relative overflow-hidden rounded-3xl border border-amber-400/40 bg-gradient-to-br from-amber-500/10 via-yellow-500/10 to-orange-500/10 p-6">
          <div className="absolute inset-0 opacity-40">
            <div className="absolute -left-20 -top-20 h-48 w-48 rounded-full bg-amber-400/30 blur-3xl" />
            <div className="absolute -bottom-20 -right-20 h-52 w-52 rounded-full bg-orange-400/30 blur-3xl" />
          </div>
          <div className="relative z-10">
            <p className="text-xs uppercase tracking-[0.3em] text-amber-800 dark:text-amber-300">
              {t("projectDetail.upcomingTitle", "Projet à venir")}
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-foreground dark:text-white">
              {countdown.isStarted
                ? t("projectDetail.upcomingNow", "Disponible maintenant")
                : t(
                    "projectDetail.upcomingSubtitle",
                    "Ouverture dans quelques instants"
                  )}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground dark:text-white/70">
              {t("projectDetail.upcomingStartsAt", "Ouverture prévue :")}{" "}
              <span className="font-semibold text-amber-900 dark:text-amber-200">
                {countdown.dateLabel}
              </span>
            </p>
            {!countdown.isStarted && (
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  {
                    label: t("projectDetail.upcomingDays", "Jours"),
                    value: countdown.days,
                  },
                  {
                    label: t("projectDetail.upcomingHours", "Heures"),
                    value: countdown.hours,
                  },
                  {
                    label: t("projectDetail.upcomingMinutes", "Minutes"),
                    value: countdown.minutes,
                  },
                  {
                    label: t("projectDetail.upcomingSeconds", "Secondes"),
                    value: countdown.seconds,
                  },
                ].map((block) => (
                  <div
                    key={block.label}
                    className="rounded-2xl border border-border bg-card p-3 text-center shadow-lg shadow-amber-500/10 dark:border-white/10 dark:bg-black/30">
                    <p className="text-2xl font-semibold text-foreground dark:text-white">
                      {String(block.value).padStart(2, "0")}
                    </p>
                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground dark:text-white/60">
                      {block.label}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {graphImages.length > 0 && (
        <div className="rounded-3xl border border-border bg-card p-4 dark:border-white/10 dark:bg-white/5">
          <p className="text-xs uppercase tracking-widest text-muted-foreground dark:text-white/60 mb-3">
            Graphes de prévision
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {graphImages.map((url, index) => (
              <div
                key={url + index}
                className="overflow-hidden rounded-2xl border border-border bg-muted/40 dark:border-white/10 dark:bg-black/20">
                <img
                  src={url}
                  alt={`Graphique de prévision ${index + 1} pour le projet ${
                    project.name
                  }`}
                  className="h-64 w-full object-cover"
                  loading="lazy"
                  crossOrigin="anonymous"
                  onError={(e) => {
                    console.error(
                      "Erreur de chargement de l'image supplémentaire:",
                      url,
                      e
                    );
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="rounded-2xl border border-border bg-card p-4 dark:border-white/10 dark:bg-white/5">
        <p className="text-xs uppercase tracking-widest text-muted-foreground dark:text-white/60">
          {t("projectDetail.fundraising")}
        </p>
        <p className="text-2xl font-semibold">
          {project.totalRaised.toLocaleString()} ${" "}
          <span className="text-sm text-muted-foreground dark:text-white/60">
            / {project.goal.toLocaleString()} $
          </span>
        </p>
        <div className="mt-3 h-2 rounded-full bg-muted dark:bg-white/10">
          <div
            className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-blue-500"
            style={{ width: `${Math.max(progress, 0)}%` }}
          />
        </div>
        <p className="mt-1 text-xs text-muted-foreground dark:text-white/60">
          {progressDisplay.toFixed(2)}
          {t("projectDetail.progressReached")}
        </p>
      </div>
      <ul className="grid gap-4 sm:grid-cols-2">
        {mockAnalytics.map((metric) => (
          <li
            key={metric.label}
            className="rounded-2xl border border-border bg-card p-4 dark:border-white/10 dark:bg-white/5">
            <p className="text-xs text-muted-foreground dark:text-white/60">{metric.label}</p>
            <p className="text-2xl font-semibold">{metric.value}</p>
          </li>
        ))}
      </ul>

      {/* Section des investissements actifs de l'utilisateur */}
      {isAuthenticated && (
        <div className="rounded-3xl border border-cyan-400/40 bg-cyan-500/5 p-6 text-foreground">
          <h3 className="text-lg font-semibold text-cyan-800 dark:text-cyan-300 mb-4">
            {t(
              "projectDetail.myActiveInvestments",
              "Mes investissements actifs sur ce projet"
            )}
          </h3>
          {userInvestments.length > 0 ? (
            <div className="space-y-4">
              {userInvestments.map((investment) => {
                const {
                  currentGain,
                  progressPercent,
                  daysRemaining,
                  isMature,
                } = calculateInvestmentProgress(investment);
                const totalReturn = investment.amount + currentGain;

                return (
                  <div
                    key={investment.id}
                    className="rounded-2xl border border-cyan-400/30 bg-muted/50 p-5 dark:bg-black/30">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="h-2 w-2 rounded-full bg-cyan-400 animate-pulse" />
                          <span className="text-sm text-muted-foreground dark:text-white/60">
                            {t("projectDetail.investedOn", "Investi le")}{" "}
                            {new Date(
                              investment.investedAt
                            ).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                          <div>
                            <p className="text-xs text-muted-foreground dark:text-white/60 mb-1">
                              {t(
                                "projectDetail.investedAmount",
                                "Montant investi"
                              )}
                            </p>
                            <p className="text-lg font-semibold">
                              {investment.amount.toFixed(2)}{" "}
                              {investment.currency}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground dark:text-white/60 mb-1">
                              {t("projectDetail.currentGain", "Gain actuel")}
                            </p>
                            <p className="text-lg font-semibold text-emerald-700 dark:text-emerald-400">
                              +{currentGain.toFixed(2)} {investment.currency}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground dark:text-white/60 mb-1">
                              {t("projectDetail.totalReturn", "Retour total")}
                            </p>
                            <p className="text-lg font-semibold text-cyan-800 dark:text-cyan-300">
                              {totalReturn.toFixed(2)} {investment.currency}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground dark:text-white/60 mb-1">
                              {t(
                                "projectDetail.timeRemaining",
                                "Temps restant"
                              )}
                            </p>
                            <p className="text-lg font-semibold">
                              {isMature
                                ? t(
                                    "projectDetail.matured",
                                    "Arrivé à maturité"
                                  )
                                : daysRemaining > 0
                                ? `${daysRemaining} ${t(
                                    "projectDetail.days",
                                    "jours"
                                  )}`
                                : t("projectDetail.completed", "Terminé")}
                            </p>
                          </div>
                        </div>
                        {/* Barre de progression */}
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs text-muted-foreground dark:text-white/60 mb-1">
                            <span>
                              {t("projectDetail.progress", "Progression")}
                            </span>
                            <span>{progressPercent.toFixed(1)}%</span>
                          </div>
                          <div className="h-2 rounded-full bg-muted overflow-hidden dark:bg-white/10">
                            <div
                              className={`h-full rounded-full transition-all ${
                                isMature
                                  ? "bg-emerald-500"
                                  : "bg-gradient-to-r from-cyan-400 to-blue-500"
                              }`}
                              style={{ width: `${progressPercent}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    {isMature && (
                      <div className="mt-3 p-3 rounded-lg bg-emerald-500/20 border border-emerald-400/50">
                        <p className="text-sm text-emerald-800 dark:text-emerald-300">
                          {t(
                            "projectDetail.maturityMessage",
                            "✅ Votre investissement est arrivé à maturité. Les fonds (montant initial + gain) seront crédités sur votre compte trading dans les prochaines heures."
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground dark:text-white/60 italic">
              {t(
                "projectDetail.noActiveInvestments",
                "Vous n'avez pas encore d'investissement actif sur ce projet."
              )}
            </p>
          )}
        </div>
      )}

      {hasOptions && selectedOption && (
        <div className="rounded-3xl border border-emerald-400/40 bg-emerald-500/5 p-6 text-foreground dark:text-white">
          <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-300 mb-2">
            {t(
              "projectDetail.investmentOptionsTitle",
              "Options d'investissement"
            )}
          </h3>
          <p className="text-xs text-muted-foreground dark:text-white/70 mb-4">
            {t(
              "projectDetail.investmentOptionsSubtitle",
              "Choisissez un montant d'investissement pour voir le gain estimé."
            )}
          </p>
          <div className="flex flex-wrap gap-3 mb-4">
            {project.investmentOptions!.map((option) => {
              const isActive = selectedOption?.id === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setSelectedOption(option)}
                  className={`rounded-2xl border px-4 py-2 text-sm transition ${
                    isActive
                      ? "border-emerald-500 bg-emerald-500/15 text-emerald-950 shadow-md shadow-emerald-500/20 dark:border-emerald-400 dark:bg-emerald-500/20 dark:text-emerald-100 dark:shadow-lg dark:shadow-emerald-500/30"
                      : "border-border bg-muted/50 text-foreground/90 hover:border-emerald-400/70 hover:bg-emerald-500/10 dark:border-white/15 dark:bg-black/30 dark:text-white/80"
                  }`}>
                  <span className="font-semibold">
                    {option.amount.toLocaleString()} $
                  </span>
                  <span
                    className={`ml-2 text-xs ${
                      isActive
                        ? "text-emerald-900/85 dark:text-emerald-200/90"
                        : "text-muted-foreground dark:text-white/70"
                    }`}>
                    ({option.returnPercentage}% • {option.durationDays}{" "}
                    {t("projectDetail.days", "j")})
                  </span>
                </button>
              );
            })}
          </div>

          <div className="grid gap-4 md:grid-cols-3 text-sm">
            <div className="rounded-2xl border border-border bg-muted/50 p-4 dark:border-white/10 dark:bg-black/30">
              <p className="text-xs uppercase tracking-widest text-muted-foreground dark:text-white/60">
                {t("projectDetail.selectedAmount", "Montant sélectionné")}
              </p>
              <p className="mt-1 text-xl font-semibold">
                {selectedOption.amount.toLocaleString()} $
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-muted/50 p-4 dark:border-white/10 dark:bg-black/30">
              <p className="text-xs uppercase tracking-widest text-muted-foreground dark:text-white/60">
                {t("projectDetail.expectedReturn", "Rendement estimé")}
              </p>
              <p className="mt-1 text-xl font-semibold text-emerald-700 dark:text-emerald-300">
                {selectedOption.expectedGain.toLocaleString()} $
              </p>
              <p className="text-xs text-muted-foreground dark:text-white/60">
                {t("projectDetail.returnPercent", "{{percent}} %", {
                  percent: selectedOption.returnPercentage,
                })}
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-muted/50 p-4 dark:border-white/10 dark:bg-black/30">
              <p className="text-xs uppercase tracking-widest text-muted-foreground dark:text-white/60">
                {t("projectDetail.duration", "Durée du projet")}
              </p>
              <p className="mt-1 text-xl font-semibold">
                {selectedOption.durationDays} {t("projectDetail.days", "jours")}
              </p>
            </div>
          </div>

          {/* Affichage des soldes des comptes */}
          {isAuthenticated && accounts.length > 0 && (
            <div className="mt-4 rounded-2xl border border-border bg-muted/50 p-4 dark:border-white/10 dark:bg-black/30">
              <p className="text-xs uppercase tracking-widest text-muted-foreground dark:text-white/60 mb-3">
                {t("projectDetail.accountBalances", "Soldes des comptes")}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {accounts
                  .filter((acc) => acc.currency === "USD")
                  .map((account) => {
                    const availableBalance =
                      account.balance - account.lockedBalance;
                    const isTrading = account.type === "trading";
                    const hasEnoughFunds =
                      availableBalance >= (selectedOption?.amount || 0);

                    return (
                      <div
                        key={account.id}
                        className={`rounded-xl border p-3 ${
                          isTrading
                            ? hasEnoughFunds
                              ? "border-emerald-400/50 bg-emerald-500/10"
                              : "border-red-400/50 bg-red-500/10"
                            : "border-border bg-muted/40 dark:border-white/10 dark:bg-white/5"
                        }`}>
                        <p className="text-xs text-muted-foreground dark:text-white/60">
                          {isTrading
                            ? t(
                                "projectDetail.tradingAccount",
                                "Compte Trading"
                              )
                            : t(
                                "projectDetail.mainAccount",
                                "Compte Principal"
                              )}
                        </p>
                        <p className="text-lg font-semibold">
                          {availableBalance.toFixed(2)} {account.currency}
                        </p>
                        {isTrading && !hasEnoughFunds && (
                          <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                            {t(
                              "projectDetail.insufficientFunds",
                              "Fonds insuffisants"
                            )}
                          </p>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {canInvest ? (
              <>
                <p className="text-xs text-muted-foreground dark:text-white/60">
                  {t(
                    "projectDetail.investDisclaimer",
                    "Cet investissement sera débité de votre compte trading."
                  )}
                </p>
                <Button
                  onClick={handleInvest}
                  disabled={isInvesting || !isAuthenticated || hasInsufficientTradingFunds}
                  className="min-w-[220px] rounded-2xl bg-gradient-to-r from-emerald-400 to-cyan-500 text-sm font-semibold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/40 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none">
                  {isInvesting
                    ? t("projectDetail.investing", "Investissement en cours...")
                    : t("projectDetail.investCta", "Investir sur ce montant")}
                </Button>
              </>
            ) : (
              <div className="w-full rounded-2xl border border-border bg-muted/40 p-4 text-center dark:border-white/20 dark:bg-white/5">
                <p className="text-sm font-medium text-foreground/90 dark:text-white/80">
                  {project.status === "closed"
                    ? t(
                        "projectDetail.projectClosed",
                        "Ce projet est clôturé. Les investissements ne sont plus acceptés."
                      )
                    : isProjectOpen
                    ? t(
                        "projectDetail.projectOpeningSoon",
                        "Ce projet ouvrira très bientôt. Merci de patienter."
                      )
                    : t(
                        "projectDetail.projectUpcoming",
                        "Ce projet n'est pas encore ouvert aux investissements."
                      )}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dialog pour le transfert de fonds */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="bg-card border-border text-foreground dark:bg-black/90 dark:border-white/10 dark:text-white">
          <DialogHeader>
            <DialogTitle>
              {t("projectDetail.transferTitle", "Transfert de fonds")}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground dark:text-white/70">
              {t(
                "projectDetail.transferDescription",
                "Vous avez suffisamment de fonds dans votre compte principal. Voulez-vous transférer {{amount}} $ vers votre compte trading pour investir ?",
                { amount: transferAmount.toLocaleString() }
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              onClick={() => setShowTransferDialog(false)}
              variant="outline"
              className="flex-1">
              {t("common.cancel", "Annuler")}
            </Button>
            <Button
              onClick={handleTransfer}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600">
              {t("projectDetail.transferAndInvest", "Transférer et investir")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog pour l'achat d'un pack */}
      <Dialog open={showPackDialog} onOpenChange={setShowPackDialog}>
        <DialogContent className="bg-card border-border text-foreground dark:bg-black/90 dark:border-white/10 dark:text-white">
          <DialogHeader>
            <DialogTitle>
              {t(
                "projectDetail.packRequiredTitle",
                "Pack d'investissement requis"
              )}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground dark:text-white/70">
              {t(
                "projectDetail.packRequiredDescription",
                "Vous n'avez pas suffisamment de fonds pour investir. Veuillez d'abord acheter un pack d'investissement pour créditer votre compte principal, puis transférer les fonds vers votre compte trading."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              onClick={() => setShowPackDialog(false)}
              variant="outline"
              className="flex-1">
              {t("common.cancel", "Annuler")}
            </Button>
            <Button
              onClick={() => {
                setShowPackDialog(false);
                navigate("/investment-packs");
              }}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600">
              {t("projectDetail.viewPacks", "Voir les packs")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="rounded-3xl border border-border bg-card p-6 text-sm text-foreground dark:border-white/10 dark:bg-white/5">
        <h3 className="text-lg font-semibold text-foreground dark:text-white">
          {t("projectDetail.sectionOperationalDetails")}
        </h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-muted/40 p-4 dark:border-white/10 dark:bg-black/20">
            <p className="text-xs uppercase tracking-widest text-muted-foreground dark:text-white/60">
              {t("projectDetail.category")}
            </p>
            <p className="text-xl font-semibold text-foreground dark:text-white">
              {project.category}
            </p>
            <p className="text-xs text-muted-foreground dark:text-white/60">
              {t("projectDetail.similarOpportunities")}
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-muted/40 p-4 dark:border-white/10 dark:bg-black/20">
            <p className="text-xs uppercase tracking-widest text-muted-foreground dark:text-white/60">
              {t("projectDetail.controlledRiskTitle")}
            </p>
            <p className="text-sm text-muted-foreground dark:text-white/70">
              {t("projectDetail.controlledRiskText")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectDetailPage;
